import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FilterPanel from "@/pages/category/components/FilterPanel";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import { categories, sortOptions } from "@/mocks/categories";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";
import type { Movie } from "@/types/movie";

const PAGE_SIZE = 12;

function applyFilters(list: Movie[], region: string, year: string, rating: string) {
  return list.filter((movie) => {
    if (region !== "all" && movie.country !== region) return false;
    if (year === "earlier" && movie.year > 2021) return false;
    if (year !== "all" && year !== "earlier" && String(movie.year) !== year) return false;
    if (rating !== "all" && movie.rating < Number(rating)) return false;
    return true;
  });
}

function sortMovies(list: Movie[], sort: string) {
  const sorted = [...list];
  if (sort === "rating") return sorted.sort((a, b) => b.rating - a.rating);
  if (sort === "popular") return sorted.sort((a, b) => b.popularity - a.popularity);
  return sorted.sort((a, b) => b.year - a.year || b.popularity - a.popularity);
}

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const category = categories.find((item) => item.key === decodeURIComponent(type || ""));

  const [region, setRegion] = useState("all");
  const [year, setYear] = useState("all");
  const [rating, setRating] = useState("all");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setPage(1);
    setFiltersOpen(false);
  }, [type]);

  const filtered = useMemo(() => {
    if (!category) return [];
    const base = movies.filter((movie) => movie.genres.includes(category.key));
    return sortMovies(applyFilters(base, region, year, rating), sort);
  }, [category, region, year, rating, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const label = category ? category.label : "全部";
  useSeo({
    title: `${label}电影分类 - 日韩伦理电影资源及最新影片`,
    description: category
      ? `${category.label}电影分类收录日韩伦理题材相关作品，包含${category.tagline}等主题影片的剧情介绍、年份、导演与评分信息，支持按地区、年份与评分筛选。`
      : "日韩伦理电影分类页，按伦理、剧情、情色与经典四个方向整理影片资料，支持筛选与排序。",
    keywords: category
      ? `${category.label}电影,日韩伦理电影,${category.label}片单,${category.label}剧情介绍,日韩电影分类`
      : "日韩伦理电影分类,伦理电影,剧情电影,情色电影,经典电影",
  });

  const handleRegion = (value: string) => {
    setRegion(value);
    setPage(1);
  };
  const handleYear = (value: string) => {
    setYear(value);
    setPage(1);
  };
  const handleRating = (value: string) => {
    setRating(value);
    setPage(1);
  };
  const resetFilters = () => {
    setRegion("all");
    setYear("all");
    setRating("all");
    setPage(1);
  };

  const activeCount =
    (region !== "all" ? 1 : 0) + (year !== "all" ? 1 : 0) + (rating !== "all" ? 1 : 0);

  if (!category) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-24 text-center md:px-8">
        <i className="ri-error-warning-line text-4xl text-primary-400"></i>
        <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950">没有找到这个分类</h1>
        <p className="mt-2 text-[13px] text-foreground-500">请从下面的分类中重新选择一个方向。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {categories.map((item) => (
            <Link
              key={item.key}
              to={`/category/${item.key}`}
              className="rounded-md border border-background-300/70 px-4 py-2 text-[12.5px] text-foreground-700 transition-colors hover:border-primary-500 hover:text-primary-200"
            >
              {item.label}电影
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-50">
      <div className="border-b border-background-200/70 bg-accent-950">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-9 pt-6 md:px-8 md:pb-11 md:pt-8">
          <nav aria-label="面包屑" className="mb-5 flex flex-wrap items-center gap-1.5 font-label text-[11px] text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-300">首页</Link>
            <i className="ri-arrow-right-s-line text-[13px]"></i>
            <span className="text-foreground-400">分类目录</span>
            <i className="ri-arrow-right-s-line text-[13px]"></i>
            <span className="text-primary-300">{category.label}</span>
          </nav>

          <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="mb-2.5 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.28em] text-accent-200">
                <span className="h-px w-6 bg-accent-400/70"></span>
                {category.en} collection
              </p>
              <h1 className="font-heading text-2xl font-semibold leading-tight text-foreground-950 md:text-[32px]">
                {category.label}电影
                <span className="ml-2 align-middle font-label text-[11px] font-normal tracking-widest text-foreground-500">
                  {filtered.length} 部
                </span>
              </h1>
              <p className="mt-3 max-w-[640px] text-[13px] leading-[1.9] text-foreground-500">{category.intro}</p>
            </div>

            <div className="flex items-center gap-2">
              <span className="flex h-12 w-12 items-center justify-center rounded-md border border-accent-700/50 bg-accent-900/50 text-accent-200">
                <i className={`${category.icon} text-xl`}></i>
              </span>
              <span className="rounded-md border border-background-300/40 px-3 py-2 font-label text-[11px] leading-relaxed text-foreground-500">
                日韩地区
                <br />
                2021 — 2025
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-7 lg:grid-cols-[248px_1fr] lg:gap-9">
          <div className="lg:sticky lg:top-[92px] lg:self-start">
            <button
              type="button"
              onClick={() => setFiltersOpen((prev) => !prev)}
              className="mb-3 flex w-full cursor-pointer items-center justify-between rounded-md border border-background-300/70 bg-background-100 px-4 py-3 text-[13px] text-foreground-700 lg:hidden"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-300"></i>
                筛选与排序
                {activeCount > 0 && (
                  <span className="rounded bg-primary-600 px-1.5 py-0.5 font-label text-[10px] text-background-50">
                    {activeCount}
                  </span>
                )}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${filtersOpen ? "rotate-180" : ""}`}></i>
            </button>

            <div className={`${filtersOpen ? "block" : "hidden"} lg:block`}>
              <FilterPanel
                region={region}
                year={year}
                rating={rating}
                resultCount={filtered.length}
                onRegionChange={handleRegion}
                onYearChange={handleYear}
                onRatingChange={handleRating}
                onReset={resetFilters}
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="mb-5 flex flex-col gap-3 border-b border-background-200/70 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="font-label text-[12px] text-foreground-500">
                共 <span className="text-secondary-300">{filtered.length}</span> 部作品 · 第 {currentPage} / {totalPages} 页
              </p>
              <div className="flex items-center gap-2">
                <span className="font-label text-[11px] text-foreground-500">排序</span>
                <div className="flex rounded-md border border-background-300/70 p-0.5">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setSort(option.value);
                        setPage(1);
                      }}
                      className={`cursor-pointer whitespace-nowrap rounded px-2.5 py-1.5 font-label text-[11px] transition-colors ${
                        sort === option.value
                          ? "bg-primary-600 text-background-50"
                          : "text-foreground-500 hover:text-foreground-800"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {pageItems.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-3 gap-y-7 sm:grid-cols-3 xl:grid-cols-4">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 py-20 text-center">
                <i className="ri-inbox-line text-3xl text-foreground-500"></i>
                <p className="mt-3 text-[13px] text-foreground-600">当前筛选条件下没有作品</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-4 cursor-pointer rounded-md border border-primary-600 px-4 py-2 text-[12px] text-primary-200 transition-colors hover:bg-primary-950/60"
                >
                  重置筛选条件
                </button>
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-10">
                <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}